"use client";

import { useState, useRef, useCallback, useEffect } from "react";
import { toast } from "react-toastify";
import { FormField, FormVersionDetails } from "../types/form-types";
import { AppConfig } from "../contexts/FormBuilderContext";
import { API_BASE_URL } from "../environments/environment";
import { useFormBuilderContext } from "../contexts/FormBuilderContext";

export interface SessionModalConfig {
  enabled: boolean;
  title?: string;
  message?: string;
  continueButtonText?: string;
  startNewButtonText?: string;
  modalClassName?: string;
  modalStyle?: React.CSSProperties;
  continueButtonClassName?: string;
  startNewButtonClassName?: string;
}

interface FormSessionData {
  sessionId: string;
  answers: Record<string, any>;
  currentPage: number;

  // Staleness detection metadata
  version?: number;           // Increments on each save (for cross-tab conflict detection)
  sessionTimestamp?: number;  // When this snapshot was last saved (for age-based staleness)
  formVersionId?: number;     // Form structure version (to detect form changes)
  formCompleted?: boolean;    // True when entire form is submitted (prevents restoration)
  tabId?: string;            // Unique tab identifier (for ignoring self-generated storage events)
}

interface UseFormSessionProps {
  formId: string | number;
  propSessionId?: string;
  propUserId?: string;
  sessionModalConfig: SessionModalConfig;
  appConfig?: AppConfig | null;
  applicationId?: string;
  formVersion?: FormVersionDetails | null;
  onFormReset?: () => void;
  onStartNewSession?: () => void;
}

interface SessionValidationResult {
  isValid: boolean;
  isStale: boolean;
  staleReason?: string;
  warnings: string[];
  sessionData: FormSessionData | null;
}

interface UseFormSessionReturn {
  // State
  sessionId: string;
  userId: string;
  showSessionModal: boolean;
  pendingSessionId: string | null;
  sessionDecisionMade: boolean;
  userContinuedSession: boolean;
  sessionSourceIsBackend: boolean;
  createdSessionsRef: { current: Set<string> };
  tabId: string; // Unique identifier for this tab

  // Handlers
  setShowSessionModal: (show: boolean) => void;
  setSessionDecisionMade: (made: boolean) => void;
  setUserContinuedSession: (continued: boolean) => void;
  handleContinueSession: (pendingSessionId: string) => Promise<void>;
  handleStartNewSession: (answers: Map<string, any>, currentPageIndex: number, setCurrentPageIndex: (index: number) => void, setAnswers: (answers: Map<string, any>) => void) => void;
  initializeSession: (sessionId?: string, skipModalCheck?: boolean) => Promise<void>;

  // LocalStorage functions
  getFormSessionKey: () => string;
  getFormSessions: () => Record<string, string | FormSessionData>;
  setFormSession: (sessionId: string, answers?: Record<string, any>, currentPage?: number) => void;
  getFormSession: () => string | null;
  getFormSessionData: () => FormSessionData | null;
  validateSessionStaleness: () => SessionValidationResult;
  updateFormSessionData: (updates: Partial<FormSessionData>, options?: { mergeAnswers?: boolean }) => void;
  removeFormSession: () => void;
  clearLocalStorageAnswers: () => void;

  // Backend functions
  checkBackendSession: (sessionIdToCheck: string) => Promise<boolean>;
}

const getPositiveInteger = (value: string | number | undefined | null): number | null => {
  const parsed = typeof value === "number" ? value : parseInt(String(value), 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

export function useFormSession({
  formId,
  propSessionId,
  propUserId,
  sessionModalConfig,
  appConfig,
  applicationId,
  formVersion,
  onFormReset,
  onStartNewSession,
}: UseFormSessionProps): UseFormSessionReturn {
  // Get backend API base URL from context
  const context = useFormBuilderContext();
  const backendApiUrl = context?.appConfig?.backend_api_base_url || context?.appConfig?.api_base_url || API_BASE_URL;

  // Helper to get/set session decision from sessionStorage (clears on page close, persists on refresh)
  // We use a flag in window to detect true page loads vs component remounts
  const getSessionDecision = useCallback(() => {
    if (typeof window === "undefined") return false;
    try {
      const key = `session_decision_${formId}`;
      // Only restore decision if this is a component remount, not a fresh page load
      // We use a window flag that gets set after first mount
      const isPageRefresh = !(window as any)[`_form_${formId}_mounted`];
      if (isPageRefresh) {
        // Fresh page load - clear any old decision
        sessionStorage.removeItem(key);
        return false;
      }
      return sessionStorage.getItem(key) === 'true';
    } catch {
      return false;
    }
  }, [formId]);

  const persistSessionDecision = useCallback((made: boolean) => {
    if (typeof window === "undefined") return;
    try {
      const key = `session_decision_${formId}`;
      if (made) {
        sessionStorage.setItem(key, 'true');
      } else {
        sessionStorage.removeItem(key);
      }
    } catch (e) {
      console.error("Error persisting session decision:", e);
    }
  }, [formId]);

  // State
  const [sessionId, setSessionId] = useState<string>("");
  const [userId, setUserId] = useState<string>("");
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [pendingSessionId, setPendingSessionId] = useState<string | null>(null);
  const [sessionDecisionMade, setSessionDecisionMadeState] = useState(() => getSessionDecision());
  const [userContinuedSession, setUserContinuedSession] = useState(false);
  const [sessionSourceIsBackend, setSessionSourceIsBackend] = useState<boolean>(false);

  // Wrapper to persist session decision to sessionStorage
  const setSessionDecisionMade = useCallback((made: boolean) => {
    setSessionDecisionMadeState(made);
    persistSessionDecision(made);
  }, [persistSessionDecision]);

  // Mark this form as having been mounted in this page session
  useEffect(() => {
    if (typeof window !== "undefined") {
      (window as any)[`_form_${formId}_mounted`] = true;
    }
  }, [formId]);

  // Refs
  const createdSessionsRef = useRef<Set<string>>(new Set());
  // Guard: prevents concurrent auto-initializations from racing each other
  const isInitializingRef = useRef(false);

  // Generate unique tab ID (persists for lifetime of this component instance)
  const tabIdRef = useRef<string>(`tab_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`);

  // Helper: Generate session ID
  const generateSessionId = useCallback(() => {
    return `session_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
  }, []);

  // Helper: Get form session key
  const getFormSessionKey = useCallback(() => `form_${formId}`, [formId]);

  // LocalStorage: Get all form sessions
  const getFormSessions = useCallback((): Record<string, string | FormSessionData> => {
    if (typeof window === "undefined") return {};
    try {
      const sessionsStr = localStorage.getItem("form_session_id");
      return sessionsStr ? JSON.parse(sessionsStr) : {};
    } catch (e) {
      console.error("Error parsing form_session_id:", e);
      return {};
    }
  }, []);

  // LocalStorage: Set form session
  const setFormSession = useCallback(
    (sessionIdToSet: string, answers: Record<string, any> = {}, currentPage: number = 0) => {
      if (typeof window === "undefined") return;
      try {
        const sessions = getFormSessions();
        sessions[getFormSessionKey()] = {
          sessionId: sessionIdToSet,
          answers,
          currentPage,
        };
        localStorage.setItem("form_session_id", JSON.stringify(sessions));
        console.log(`Set session for ${getFormSessionKey()}:`, { sessionId: sessionIdToSet, answers, currentPage });
      } catch (e) {
        console.error("Error setting form session:", e);
      }
    },
    [getFormSessions, getFormSessionKey]
  );

  // LocalStorage: Get form session
  const getFormSession = useCallback((): string | null => {
    const sessions = getFormSessions();
    const sessionData = sessions[getFormSessionKey()];

    if (typeof sessionData === "string") {
      setFormSession(sessionData, {}, 0);
      return sessionData;
    } else if (sessionData && typeof sessionData === "object") {
      return sessionData.sessionId;
    }

    return null;
  }, [getFormSessions, getFormSessionKey, setFormSession]);

  // LocalStorage: Get form session data
  const getFormSessionData = useCallback((): FormSessionData | null => {
    const sessions = getFormSessions();
    const sessionData = sessions[getFormSessionKey()];

    if (typeof sessionData === "string") {
      const newData: FormSessionData = {
        sessionId: sessionData,
        answers: {},
        currentPage: 0,
      };
      setFormSession(sessionData, {}, 0);
      return newData;
    } else if (sessionData && typeof sessionData === "object") {
      return sessionData as FormSessionData;
    }

    return null;
  }, [getFormSessions, getFormSessionKey, setFormSession]);

  // LocalStorage: Validate session staleness
  const validateSessionStaleness = useCallback((): SessionValidationResult => {
    const sessionData = getFormSessionData();
    const warnings: string[] = [];

    // No session data found
    if (!sessionData) {
      return {
        isValid: false,
        isStale: false,
        sessionData: null,
        warnings: [],
      };
    }

    // Check 1: Form already completed
    if (sessionData.formCompleted === true) {
      return {
        isValid: false,
        isStale: true,
        staleReason: "Form was already submitted",
        sessionData,
        warnings: ["This form has already been completed and submitted"],
      };
    }

    // Check 2: Age-based staleness (data older than 7 days)
    const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
    if (sessionData.sessionTimestamp) {
      const age = Date.now() - sessionData.sessionTimestamp;
      if (age > MAX_AGE_MS) {
        const daysOld = Math.floor(age / (24 * 60 * 60 * 1000));
        warnings.push(`Saved data is ${daysOld} day${daysOld > 1 ? 's' : ''} old`);
      }
    }

    // Check 3: Form version mismatch (form structure changed)
    if (sessionData.formVersionId && formVersion?.id && sessionData.formVersionId !== formVersion.id) {
      warnings.push("Form structure has changed since this data was saved");
    }

    // Check 4: No answers and on first page (essentially empty session)
    const hasAnswers = sessionData.answers && Object.keys(sessionData.answers).length > 0;
    const isOnFirstPage = sessionData.currentPage === 0;
    if (!hasAnswers && isOnFirstPage) {
      // Empty session, perfectly valid
      return {
        isValid: true,
        isStale: false,
        sessionData,
        warnings: [],
      };
    }

    // Data is valid but may have warnings
    return {
      isValid: true,
      isStale: warnings.length > 0,
      staleReason: warnings.length > 0 ? warnings.join("; ") : undefined,
      sessionData,
      warnings,
    };
  }, [getFormSessionData, formVersion]);

  // LocalStorage: Update form session data
  const updateFormSessionData = useCallback(
    (updates: Partial<FormSessionData>, options?: { mergeAnswers?: boolean }) => {
      if (typeof window === "undefined") return;
      try {
        const sessions = getFormSessions();
        const currentData = getFormSessionData();

        if (!currentData) {
          console.error("No session data to update");
          return;
        }

        // Auto-increment version for staleness detection
        const currentVersion = currentData.version || 0;

        // If answers are being updated, reset formCompleted flag (unless explicitly set)
        const shouldResetCompleted = updates.answers !== undefined && updates.formCompleted === undefined;

        // Handle answers based on merge mode
        // mergeAnswers: true (default) = merge with existing answers (preserves other pages)
        // mergeAnswers: false = replace answers entirely (allows clearing/removing keys)
        const shouldMergeAnswers = options?.mergeAnswers !== false; // Default to true
        const finalAnswers = updates.answers !== undefined
          ? (shouldMergeAnswers
              ? { ...(currentData.answers || {}), ...updates.answers }  // MERGE mode
              : updates.answers)                                        // REPLACE mode
          : currentData.answers;

        const updatedData: FormSessionData = {
          ...currentData,
          ...updates,
          answers: finalAnswers,
          // Auto-track staleness metadata
          version: currentVersion + 1,
          sessionTimestamp: Date.now(),
          formVersionId: formVersion?.id || currentData.formVersionId,
          formCompleted: shouldResetCompleted ? false : (updates.formCompleted ?? currentData.formCompleted ?? false),
          tabId: tabIdRef.current, // Track which tab made this update
        };

        sessions[getFormSessionKey()] = updatedData;
        localStorage.setItem("form_session_id", JSON.stringify(sessions));
        // Reduced logging - comment out to reduce console noise
        // console.log(`Updated session data for ${getFormSessionKey()}:`, updatedData);
      } catch (e) {
        console.error("Error updating form session data:", e);
      }
    },
    [getFormSessions, getFormSessionKey, getFormSessionData, formVersion]
  );

  // LocalStorage: Clear answers
  const clearLocalStorageAnswers = useCallback(() => {
    updateFormSessionData(
      { answers: {} },
      { mergeAnswers: false } // REPLACE mode: fully clear all answers
    );
  }, [updateFormSessionData]);

  // LocalStorage: Remove form session
  const removeFormSession = useCallback(() => {
    if (typeof window === "undefined") return;
    try {
      const sessions = getFormSessions();
      delete sessions[getFormSessionKey()];
      localStorage.setItem("form_session_id", JSON.stringify(sessions));
      // Also clear session decision when session is removed
      persistSessionDecision(false);
      console.log(`Removed session for ${getFormSessionKey()}`);
    } catch (e) {
      console.error("Error removing form session:", e);
    }
  }, [getFormSessions, getFormSessionKey, persistSessionDecision]);

  // Backend: Get session data
  const getBackendSessionData = useCallback(
    async (sessionIdToCheck: string): Promise<any | null> => {
      try {
        const fetchUrl = `${backendApiUrl}/api/v1/form-entries/session?session_id=${sessionIdToCheck}`;
        const response = await fetch(fetchUrl, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });

        if (response.ok) {
          const result = await response.json();
          const data = result.data || result;
          return data;
        }
        return null;
      } catch (error) {
        console.error("Error fetching backend session data:", error);
        return null;
      }
    },
    [backendApiUrl]
  );

  // Backend: Check if session exists
  const checkBackendSession = useCallback(
    async (sessionIdToCheck: string): Promise<boolean> => {
      const data = await getBackendSessionData(sessionIdToCheck);
      return data && (data.last_page_answered !== undefined || data.page_number !== undefined);
    },
    [getBackendSessionData]
  );

  // Backend: Create form entry
  const createFormEntry = useCallback(
    async (sessionIdToUse: string, formVersionId: number | undefined): Promise<boolean> => {
      try {
        console.log("📝 Creating form entry for session:", sessionIdToUse, "formVersionId:", formVersionId, "applicationId:", applicationId);
        const createUrl = `${backendApiUrl}/api/v1/form-entries/create`;

        // Convert formId to integer (handles string | number from props)
        const formIdAsInt = typeof formId === 'string' ? parseInt(formId, 10) : formId;

        const payload: any = {
          form_version_id: formVersionId,
          form_id: !isNaN(formIdAsInt) ? formIdAsInt : null,  // Ensure valid integer or null
          status: "in_progress",
          session_id: sessionIdToUse,
          last_page_answered: 0,
          application_id: applicationId,
        };

        Object.keys(payload).forEach((key) => (payload[key] === undefined || payload[key] === null) && delete payload[key]);

        console.log("📝 Form entry payload:", payload);

        const response = await fetch(createUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        if (response.ok) {
          const result = await response.json();
          console.log("✅ Form entry created successfully:", result);
          return true;
        } else {
          const errorText = await response.text();
          console.warn("⚠️ Failed to create form entry. Status:", response.status, "Error:", errorText);
          return false;
        }
      } catch (error) {
        console.error("❌ Error creating form entry:", error);
        return false;
      }
    },
    [applicationId, backendApiUrl, formId]
  );

  // Handler: Initialize session
  // Logic: check localStorage first → if found, use it; if not, generate new one.
  // Always check backend after resolving the session ID:
  //   - exists on backend → skip creation (session was already registered)
  //   - not on backend    → create the form entry once (guarded by createdSessionsRef)
  // isInitializingRef blocks concurrent auto-init calls that arrive while an async
  // init is already in-flight (React re-renders can fire the useEffect multiple times).
  const initializeSession = useCallback(
    async (currentSessionId?: string, skipModalCheck: boolean = false) => {
      if (typeof window === "undefined") return;

      // Block concurrent auto-inits. Explicit calls (e.g. handleStartNewSession) always proceed.
      if (!currentSessionId) {
        if (isInitializingRef.current) {
          console.log("🔒 [initializeSession] Blocked concurrent call - already initializing");
          return;
        }
        isInitializingRef.current = true;
      }

      console.log("🔄 [initializeSession] START", {
        explicit: currentSessionId || "(auto)",
        skipModalCheck,
      });

      try {
        let sessionIdToUse: string;

        // ── Step 1: resolve session ID ──────────────────────────────────────────
        if (currentSessionId) {
          // Explicit call from handleStartNewSession — use as-is
          sessionIdToUse = currentSessionId;
          console.log("📌 [initializeSession] Using explicit session:", sessionIdToUse);
        } else if (propSessionId) {
          // Prop-managed (backend sets the session ID)
          sessionIdToUse = propSessionId;
          setSessionSourceIsBackend(true);
          console.log("📌 [initializeSession] Using prop session:", sessionIdToUse);
        } else {
          // Auto-managed: check localStorage first
          const storedSessionId = getFormSession();
          console.log("📦 [initializeSession] localStorage:", storedSessionId ? `found → ${storedSessionId}` : "empty");

          if (storedSessionId) {
            sessionIdToUse = storedSessionId;
            console.log("♻️ [initializeSession] Reusing stored session:", sessionIdToUse);
            // Treat reused session as "user continued" so FormRenderer fetches saved answers
            setUserContinuedSession(true);
            setSessionDecisionMade(true);
          } else {
            sessionIdToUse = generateSessionId();
            setFormSession(sessionIdToUse, {}, 0);
            console.log("🆕 [initializeSession] No stored session — generated new:", sessionIdToUse);
            // Treat new session as "user started new" so FormRenderer skips answer fetch
            setUserContinuedSession(false);
            setSessionDecisionMade(true);
          }
        }

        setSessionId(sessionIdToUse);

        // ── Step 2: check backend ───────────────────────────────────────────────
        console.log("🔍 [initializeSession] Checking backend for session:", sessionIdToUse);
        const sessionExists = await checkBackendSession(sessionIdToUse);
        console.log("🔍 [initializeSession] Backend result:", sessionExists ? "EXISTS (skip create)" : "NOT FOUND (will create)");

        // ── Step 3: create if missing ───────────────────────────────────────────
        if (sessionExists) {
          console.log("✅ [initializeSession] Session already on backend, nothing to create");
        } else if (createdSessionsRef.current.has(sessionIdToUse)) {
          console.log("⏭️ [initializeSession] Creation already attempted this page load:", sessionIdToUse);
        } else {
          createdSessionsRef.current.add(sessionIdToUse);
          console.log("📝 [initializeSession] Creating backend entry for:", sessionIdToUse);
          try {
            const existingFormVersionId = getPositiveInteger(formVersion?.id);
            if (existingFormVersionId) {
              console.log("✅ [initializeSession] Using loaded form version:", existingFormVersionId, "— calling createFormEntry");
              await createFormEntry(sessionIdToUse, existingFormVersionId);
            } else {
              const formIdForLookup = getPositiveInteger(formId);
              if (!formIdForLookup) {
                console.log("⏭️ [initializeSession] Skipping backend entry creation until a numeric form id or form version is available");
              } else {
                console.log("📋 [initializeSession] Fetching form version for formId:", formIdForLookup);
                const response = await fetch(`${backendApiUrl}/api/v1/forms/get-published-version/${formIdForLookup}`);
                if (response.ok) {
                  const formVersionResponse = await response.json();
                  if (formVersionResponse.code === 200 && formVersionResponse.data?.id) {
                    const formVersionId = formVersionResponse.data.id;
                    console.log("✅ [initializeSession] Got form version:", formVersionId, "— calling createFormEntry");
                    await createFormEntry(sessionIdToUse, formVersionId);
                  } else {
                    console.error("❌ [initializeSession] Could not parse form version from response");
                  }
                } else {
                  console.error("❌ [initializeSession] Form version fetch failed, status:", response.status);
                }
              }
            }
          } catch (error) {
            console.error("❌ [initializeSession] Exception during form entry creation:", error);
          }
        }

        // ── Step 4: resolve user ID ─────────────────────────────────────────────
        let currentUserId = propUserId || "unknown";
        if (!propUserId) {
          try {
            const userStr = localStorage.getItem("user");
            if (userStr) {
              const user = JSON.parse(userStr);
              currentUserId = user.id || "unknown";
            }
          } catch (e) {
            // ignore
          }
        }
        setUserId(currentUserId);

        console.log("✅ [initializeSession] DONE — sessionId:", sessionIdToUse);

      } finally {
        // Always release the lock so a subsequent explicit call can run
        if (!currentSessionId) isInitializingRef.current = false;
      }
    },
    [
      propSessionId,
      propUserId,
      formId,
      formVersion?.id,
      backendApiUrl,
      checkBackendSession,
      createFormEntry,
      generateSessionId,
      getFormSession,
      setFormSession,
      setSessionDecisionMade,
    ]
  );

  // Handler: Continue session
  const handleContinueSession = useCallback(
    async (sessionIdToContinue: string) => {
      console.log(`User chose to continue existing session for ${getFormSessionKey()}:`, sessionIdToContinue);
      if (sessionIdToContinue) {
        setSessionId(sessionIdToContinue);
        setUserContinuedSession(true);
        setSessionDecisionMade(true);

        // Don't calculate pages here - let FormRenderer handle page navigation based on formAnswers and form structure
        // Just get the backend session data for logging purposes
        const backendSessionData = await getBackendSessionData(sessionIdToContinue);
        if (backendSessionData && backendSessionData.last_page_answered !== undefined) {
          const lastPageAnswered = backendSessionData.last_page_answered;
          console.log("📄 Last page answered from backend:", lastPageAnswered);
        }
      }
      setShowSessionModal(false);
      toast.success("Continuing where you left off...");
    },
    [getFormSessionKey, getBackendSessionData]
  );

  // Handler: Start new session
  const handleStartNewSession = useCallback(
    (answers: Map<string, any>, currentPageIndex: number, setCurrentPageIndex: (index: number) => void, setAnswers: (answers: Map<string, any>) => void) => {
      console.log(`User chose to start a new session`);

      // Helper to preserve hidden fields
      const preserveHiddenFields = (currentAnswers: Map<string, any>): Map<string, any> => {
        if (!formVersion?.form_structure?.fields) {
          return new Map();
        }

        const preservedAnswers = new Map<string, any>();
        const hiddenFieldIds = formVersion.form_structure.fields
          .filter((f: FormField) => f.type === "hidden")
          .map((f) => f.id);

        hiddenFieldIds.forEach((fieldId) => {
          if (currentAnswers.has(fieldId)) {
            preservedAnswers.set(fieldId, currentAnswers.get(fieldId));
            console.log(`✅ Preserving hidden field value for ${fieldId}:`, currentAnswers.get(fieldId));
          }
        });

        return preservedAnswers;
      };

      // CASE 1: Backend-managed session
      if (sessionSourceIsBackend) {
        console.log("Backend-managed session: calling onStartNewSession callback");
        if (onStartNewSession) {
          onStartNewSession();
        }

        const preservedAnswers = preserveHiddenFields(answers);
        setAnswers(preservedAnswers);
        setCurrentPageIndex(0);
        setUserContinuedSession(false);
        setSessionDecisionMade(true);
        setShowSessionModal(false);
        setPendingSessionId(null);

        onFormReset?.();

        toast.info("Starting a new form...");
        return;
      }

      // CASE 2: Auto-managed session
      console.log(`Auto-managed session: generating new session for ${getFormSessionKey()}`);

      removeFormSession();

      const newSessionId = generateSessionId();
      setFormSession(newSessionId, {}, 0);

      setSessionId(newSessionId);
      setUserContinuedSession(false);
      setSessionDecisionMade(true);
      setShowSessionModal(false);
      setPendingSessionId(null);

      const preservedAnswers = preserveHiddenFields(answers);
      setAnswers(preservedAnswers);
      setCurrentPageIndex(0);
      console.log("Cleared form answers and reset to first page, preserved hidden fields");

      onFormReset?.();

      // Initialize the new session (create backend form entry)
      // skipModalCheck=true to prevent modal from appearing after user clicked "Start Over"
      console.log("🔄 Calling initializeSession for new session:", newSessionId);
      initializeSession(newSessionId, true);

      toast.info("Starting a new form...");
    },
    [formVersion, sessionSourceIsBackend, onStartNewSession, onFormReset, removeFormSession, generateSessionId, getFormSessionKey, setFormSession, initializeSession]
  );

  return {
    // State
    sessionId,
    userId,
    showSessionModal,
    pendingSessionId,
    sessionDecisionMade,
    userContinuedSession,
    sessionSourceIsBackend,
    createdSessionsRef,
    tabId: tabIdRef.current,

    // Handlers
    setShowSessionModal,
    setSessionDecisionMade,
    setUserContinuedSession,
    handleContinueSession,
    handleStartNewSession,
    initializeSession,

    // LocalStorage functions
    getFormSessionKey,
    getFormSessions,
    setFormSession,
    getFormSession,
    getFormSessionData,
    validateSessionStaleness,
    updateFormSessionData,
    removeFormSession,
    clearLocalStorageAnswers,

    // Backend functions
    checkBackendSession,
  };
}
